import { Select, Table as BaseTable } from '@octano/global-ui';
import { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import { Table as TableCollapsable } from '../../../../../../components/table-collapsable';
import SectionTitle from '../../../../../../components/text/SectionTitle';
import {
  PeriodItem,
  StudyPlanVersionOfferItem,
} from '../../../api/partnership';
import { usePartnershipColumns } from '../../hooks/usePartnershipColumns';
import {
  usePartnershipOfferColumns,
  OfferRow,
} from '../../hooks/usePartnershipOfferColumns';
import { YEAR_OPTIONS } from '../../../constants';

const prefix = 'tariffAndTuition.partnerships';

const makeDiscountKey = (periodId: number, offerId: number) =>
  `${periodId}-${offerId}`;

interface FormValues {
  name: string;
  typeId: string;
  description: string;
  applyAutomatically: boolean;
  discounts: { [key: string]: string };
}

interface PartnershipDiscountsSectionProps {
  periods: PeriodItem[];
  offers: StudyPlanVersionOfferItem[];
  loadedDiscounts: { [key: string]: string };
  yearFilter: string;
  onYearFilterChange: (value: string) => void;
}

const PartnershipDiscountsSection = ({
  periods,
  offers,
  loadedDiscounts,
  yearFilter,
  onYearFilterChange,
}: PartnershipDiscountsSectionProps) => {
  const { t } = useTranslation();
  const { control } = useFormContext<FormValues>();

  const [academicOfferFilter, setAcademicOfferFilter] = useState<string>('all');

  const columns = usePartnershipColumns();
  const offerColumns = usePartnershipOfferColumns({ control });

  const academicOfferOptions = useMemo(() => {
    const uniquePlans = new Map<number, string>();
    offers.forEach((offer) => {
      const planId = offer.studyPlanVersion?.studyPlan?.id;
      const planName = offer.studyPlanVersion?.studyPlan?.name;
      if (planId && planName && !uniquePlans.has(planId)) {
        uniquePlans.set(planId, planName);
      }
    });
    return [
      { value: 'all', label: t(`${prefix}.allOffers`) },
      ...Array.from(uniquePlans.entries()).map(([id, name]) => ({
        value: String(id),
        label: name,
      })),
    ];
  }, [offers, t]);

  const filteredOffers = useMemo(() => {
    if (academicOfferFilter === 'all') {
      return offers;
    }
    return offers.filter(
      (offer) =>
        String(offer.studyPlanVersion?.studyPlan?.id) === academicOfferFilter,
    );
  }, [offers, academicOfferFilter]);

  const getOfferRowsForPeriod = useCallback(
    (period: PeriodItem): OfferRow[] => {
      return filteredOffers.map((offer) => {
        const discountKey = makeDiscountKey(period.id, offer.id);
        return {
          ...offer,
          discountKey,
          defaultValue: loadedDiscounts[discountKey] || '',
        };
      });
    },
    [filteredOffers, loadedDiscounts],
  );

  const secondColumn = useMemo(
    () => ({
      tdClassName: 'px-4',
      columnName: 'offers',
      headerText: '',
      cellFormat: ({ row }: { row: PeriodItem }) => (
        <BaseTable<OfferRow>
          borderless={false}
          color="transparent"
          striped
          columns={offerColumns}
          data={getOfferRowsForPeriod(row)}
          noResultsText={t(`${prefix}.noOffers`)}
        />
      ),
    }),
    [offerColumns, getOfferRowsForPeriod, t],
  );

  return (
    <>
      <Row className="mb-3">
        <Col xs="12">
          <SectionTitle text={t(`${prefix}.discounts`)} />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col xs="12" md="6">
          <Select
            name="yearFilter"
            label={t(`${prefix}.year`)}
            options={YEAR_OPTIONS}
            value={YEAR_OPTIONS.find((o) => o.value === yearFilter)}
            onChange={(option: any) =>
              onYearFilterChange(option?.value || 'last5')
            }
          />
        </Col>
        <Col xs="12" md="6">
          <Select
            name="academicOfferFilter"
            label={t(`${prefix}.academicOffer`)}
            options={academicOfferOptions}
            value={academicOfferOptions.find(
              (o) => o.value === academicOfferFilter,
            )}
            onChange={(option: any) =>
              setAcademicOfferFilter(option?.value || 'all')
            }
          />
        </Col>
      </Row>

      <Row>
        <Col xs="12">
          <TableCollapsable<PeriodItem>
            columns={columns}
            data={periods}
            secondColumn={secondColumn}
            noResultsText={t(`${prefix}.noPeriods`)}
          />
        </Col>
      </Row>
    </>
  );
};

export default PartnershipDiscountsSection;
